import {
  Component,
  effect,
  inject,
  model,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DIALOGS } from '@progress/kendo-angular-dialog';
import {
  DropDownListComponent,
  DropDownsModule,
} from '@progress/kendo-angular-dropdowns';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { InputsModule, KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { EditIconComponent } from '@shared/icons/edit-icon.component';
import { ApiService } from '@services/api/api.service';
import { AuthService } from '@services/auth/auth.service';
import { CommonService } from '@services/common/common.service';
import { forkJoin, Observable } from 'rxjs';

interface OrganizationContactDtoModel {
  contactId?: string | null;
  accountName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  emailAddress?: string | null;
  phoneNumber?: string | null;
  phoneNumberExtension?: string | null;
  isPrimaryContact: boolean;
  isSupportContact: boolean;
}

interface ContactDropdownItem {
  id: string;
  name: string;
}

type ContactTarget = 'primary' | 'support';

interface ContactFormValue {
  contactId: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
  extension: string;
}

interface UpdateOrganizationContactDtoModel {
  contactId: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
  phoneNumberExtension: string;
  isPrimaryContact: boolean;
  isSupportContact: boolean;
}

interface CreateOrganizationContactDtoModel {
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
  phoneNumberExtension: string;
  isPrimaryContact: boolean;
  isSupportContact: boolean;
}

interface ConflictContext {
  target: ContactTarget;
  formValue: ContactFormValue;
  candidates: OrganizationContactDtoModel[];
}

@Component({
  selector: 'app-account-contact',
  imports: [
    ReactiveFormsModule,
    KENDO_BUTTON,
    KENDO_DIALOGS,
    KENDO_LOADER,
    DropDownsModule,
    InputsModule,
    KENDO_INPUTS,
    EditIconComponent,
  ],
  templateUrl: './account-contact.component.html',
  styleUrls: ['./account-contact.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AccountContactComponent {
  private readonly createNewContactValue = '__create_new_contact__';
  public phoneMask: string = '(000) 000-0000';

  private apiService = inject(ApiService);
  private authService = inject(AuthService);
  private commonService = inject(CommonService);
  private fb = inject(FormBuilder);

  @ViewChild('primaryContactDropdown')
  private primaryContactDropdown?: DropDownListComponent;

  @ViewChild('supportContactDropdown')
  private supportContactDropdown?: DropDownListComponent;

  show = model(false);
  isDialogOpen = false;
  isEditable = false;
  isLoadingContacts = false;
  isSavingContacts = false;
  editPrimaryContact = model(false);
  editSupportContact = model(false);
  primaryContact: OrganizationContactDtoModel | null = null;
  supportContact: OrganizationContactDtoModel | null = null;
  dialogMessage = '';
  dialogCandidates: OrganizationContactDtoModel[] = [];
  showCandidateSelection = false;
  selectedDialogContactId = '';
  private pendingConflict: ConflictContext | null = null;
  private existingContactIds = new Set<string>();

  primaryContactFormGroup: FormGroup = this.buildContactFormGroup();
  supportContactFormGroup: FormGroup = this.buildContactFormGroup();

  private organizationContacts: OrganizationContactDtoModel[] = [];
  public contacts: ContactDropdownItem[] = [];

  constructor() {
    effect(() => {
      if (this.show()) {
        this.loadContactsForSelectedAccount();
      }
    });

    this.primaryContactFormGroup.valueChanges.subscribe((value) => {
      if (
        !this.areFormsLinked(
          this.primaryContactFormGroup,
          this.supportContactFormGroup,
        )
      ) {
        return;
      }

      this.patchLinkedEditableFields(this.supportContactFormGroup, value);
      this.syncLinkedDisplayFromForm(this.primaryContactFormGroup, value);
    });

    this.supportContactFormGroup.valueChanges.subscribe((value) => {
      if (
        !this.areFormsLinked(
          this.supportContactFormGroup,
          this.primaryContactFormGroup,
        )
      ) {
        return;
      }

      this.patchLinkedEditableFields(this.primaryContactFormGroup, value);
      this.syncLinkedDisplayFromForm(this.supportContactFormGroup, value);
    });
  }

  private patchLinkedEditableFields(
    formGroup: FormGroup,
    value: Partial<ContactFormValue>,
  ): void {
    formGroup.patchValue(
      {
        firstName: value.firstName ?? '',
        lastName: value.lastName ?? '',
        emailAddress: value.emailAddress ?? '',
        phoneNumber: value.phoneNumber ?? '',
        extension: value.extension ?? '',
      },
      { emitEvent: false },
    );
  }

  open(): void {
    this.show.set(true);
    this.isEditable = false;
  }

  close(): void {
    this.show.set(false);
    this.isEditable = false;
    this.editPrimaryContact.set(false);
    this.editSupportContact.set(false);
  }

  toggleEdit(): void {
    this.isEditable = !this.isEditable;
  }

  canManageContacts(): boolean {
    const selectedAccount = this.authService.selectedAccount();
    const isCustomerAdmin = selectedAccount?.roleId === 1;
    return isCustomerAdmin;
  }

  isBusy(): boolean {
    return this.isLoadingContacts || this.isSavingContacts;
  }

  save(): void {
    if (this.isBusy()) {
      return;
    }

    if (!this.canManageContacts()) {
      this.finishSave();
      return;
    }

    const accountId = this.authService.selectedAccount()?.accountId as
      | string
      | undefined;

    if (this.editPrimaryContact() && this.primaryContactFormGroup.invalid) {
      this.primaryContactFormGroup.markAllAsTouched();
      return;
    }

    if (this.editSupportContact() && this.supportContactFormGroup.invalid) {
      this.supportContactFormGroup.markAllAsTouched();
      return;
    }

    if (this.editPrimaryContact()) {
      if (!this.persistContactForTarget('primary')) {
        return;
      }
    }

    if (this.editSupportContact()) {
      if (!this.persistContactForTarget('support')) {
        return;
      }
    }

    const updates = this.buildContactUpdates();
    const contactsToAdd = this.buildContactAdds();
    if (accountId && (updates.length || contactsToAdd.length)) {
      const saveRequest = this.createSaveRequest(
        accountId,
        updates,
        contactsToAdd,
      );
      this.isSavingContacts = true;
      saveRequest.subscribe({
        next: () => {
          this.isSavingContacts = false;
          this.commonService.successNotification(
            'Account contacts updated successfully.',
            'center',
            'bottom',
          );
          this.finishSave();
        },
        error: (error) => {
          this.isSavingContacts = false;
          this.commonService.errorNotification(
            'There was an error updating account contacts.',
            'center',
            'bottom',
          );
          console.error('Failed to update organization contacts', error);
        },
      });
      return;
    }

    this.finishSave();
  }

  private createSaveRequest(
    accountId: string,
    updates: UpdateOrganizationContactDtoModel[],
    contactsToAdd: CreateOrganizationContactDtoModel[],
  ): Observable<unknown> {
    if (updates.length && contactsToAdd.length) {
      return forkJoin([
        this.apiService.putRequest(
          `/dynamics/organizationcontacts/${accountId}`,
          updates,
        ),
        this.apiService.postRequest(
          `/Dynamics/organizationcontacts/${accountId}`,
          contactsToAdd,
        ),
      ]);
    }

    if (updates.length) {
      return this.apiService.putRequest(
        `/dynamics/organizationcontacts/${accountId}`,
        updates,
      );
    }

    return this.apiService.postRequest(
      `/Dynamics/organizationcontacts/${accountId}`,
      contactsToAdd,
    );
  }

  private finishSave(): void {
    this.isEditable = false;
    this.editPrimaryContact.set(false);
    this.editSupportContact.set(false);
    this.show.set(false);
  }

  editPrimary() {
    if (!this.canManageContacts() || this.isBusy()) {
      return;
    }

    if (this.editPrimaryContact()) {
      this.cancelEdit('primary');
      return;
    }

    this.editPrimaryContact.set(true);
    this.patchFormFromContact(
      this.primaryContactFormGroup,
      this.primaryContact,
    );
    if (!this.primaryContact) {
      this.primaryContactFormGroup
        .get('contactId')
        ?.setValue(this.createNewContactValue, { emitEvent: false });
    }
    this.primaryContactFormGroup.markAsPristine();
    this.primaryContactFormGroup.markAsUntouched();
  }

  createContact(target: 'primary' | 'support'): void {
    if (!this.canManageContacts() || this.isBusy()) {
      return;
    }

    const formGroup =
      target === 'primary'
        ? this.primaryContactFormGroup
        : this.supportContactFormGroup;
    const pairedFormGroup =
      target === 'primary'
        ? this.supportContactFormGroup
        : this.primaryContactFormGroup;

    const dropdown =
      target === 'primary'
        ? this.primaryContactDropdown
        : this.supportContactDropdown;

    const shouldSyncPair = this.areFormsLinked(formGroup, pairedFormGroup);

    formGroup.get('contactId')?.setValue(this.createNewContactValue);

    this.patchFormFromContact(formGroup, null);
    formGroup.markAsPristine();
    formGroup.markAsUntouched();
    formGroup.get('contactId')?.setValue(this.createNewContactValue);

    if (shouldSyncPair) {
      this.patchFormFromContact(pairedFormGroup, null);
      pairedFormGroup.markAsPristine();
      pairedFormGroup.markAsUntouched();
      pairedFormGroup
        .get('contactId')
        ?.setValue(this.createNewContactValue, { emitEvent: false });
    }

    dropdown?.toggle(false);
  }

  editSupport() {
    if (!this.canManageContacts() || this.isBusy()) {
      return;
    }

    if (this.editSupportContact()) {
      this.cancelEdit('support');
      return;
    }

    this.editSupportContact.set(true);
    this.patchFormFromContact(
      this.supportContactFormGroup,
      this.supportContact,
    );
    if (!this.supportContact) {
      this.supportContactFormGroup
        .get('contactId')
        ?.setValue(this.createNewContactValue, { emitEvent: false });
    }
    this.supportContactFormGroup.markAsPristine();
    this.supportContactFormGroup.markAsUntouched();
  }

  private cancelEdit(target: ContactTarget): void {
    if (target === 'primary') {
      this.patchFormFromContact(
        this.primaryContactFormGroup,
        this.primaryContact,
      );
      this.primaryContactFormGroup.markAsPristine();
      this.primaryContactFormGroup.markAsUntouched();
      this.editPrimaryContact.set(false);
      return;
    }

    this.patchFormFromContact(
      this.supportContactFormGroup,
      this.supportContact,
    );
    this.supportContactFormGroup.markAsPristine();
    this.supportContactFormGroup.markAsUntouched();
    this.editSupportContact.set(false);
  }

  onPrimaryContactSelectionChange(contactId: string): void {
    if (!this.canManageContacts() || this.isBusy()) {
      return;
    }

    if (contactId === this.createNewContactValue) {
      return;
    }

    const selected = this.findContactById(contactId);
    this.primaryContact = selected;
    this.patchFormFromContact(this.primaryContactFormGroup, selected);
  }

  onSupportContactSelectionChange(contactId: string): void {
    if (!this.canManageContacts() || this.isBusy()) {
      return;
    }

    if (contactId === this.createNewContactValue) {
      return;
    }

    const selected = this.findContactById(contactId);
    this.supportContact = selected;
    this.patchFormFromContact(this.supportContactFormGroup, selected);
  }

  private syncLinkedDisplayFromForm(
    formGroup: FormGroup,
    value: Partial<ContactFormValue>,
  ): void {
    const contactId = formGroup.get('contactId')?.value as string | undefined;
    if (!contactId || contactId === this.createNewContactValue) {
      return;
    }

    const linked = this.findContactById(contactId);
    if (!linked) {
      return;
    }

    const displayContact: OrganizationContactDtoModel = {
      ...linked,
      firstName: value.firstName ?? linked.firstName ?? '',
      lastName: value.lastName ?? linked.lastName ?? '',
      emailAddress: value.emailAddress ?? linked.emailAddress ?? '',
      phoneNumber: this.maskPhoneNumber(
        value.phoneNumber ?? linked.phoneNumber ?? '',
      ),
      phoneNumberExtension:
        value.extension ?? linked.phoneNumberExtension ?? '',
    };

    this.primaryContact = displayContact;
    this.supportContact = displayContact;
  }

  private loadContactsForSelectedAccount(): void {
    const selectedAccount = this.authService.selectedAccount();
    const accountId = selectedAccount?.accountId as string | undefined;

    if (!accountId) {
      this.isLoadingContacts = false;
      this.contacts = [];
      this.organizationContacts = [];
      this.primaryContact = null;
      this.supportContact = null;
      this.primaryContactFormGroup.reset();
      this.supportContactFormGroup.reset();
      return;
    }

    this.getOrganizationContacts(accountId);
  }

  private getOrganizationContacts(accountId: string): void {
    this.isLoadingContacts = true;
    this.apiService
      .getRequest(`/dynamics/organizationcontacts/${accountId}`)
      .subscribe({
        next: (response: OrganizationContactDtoModel[] | null | undefined) => {
          this.isLoadingContacts = false;
          const contactList = response ?? [];
          this.organizationContacts = contactList;
          this.existingContactIds = new Set(
            contactList
              .map((contact) => contact.contactId ?? '')
              .filter((contactId) => !!contactId),
          );
          this.primaryContact = null;
          this.supportContact = null;
          for (const contact of contactList) {
            this.primaryContact = contact.isPrimaryContact
              ? contact
              : this.primaryContact;
            this.supportContact = contact.isSupportContact
              ? contact
              : this.supportContact;
          }

          this.contacts = this.buildDropdownContacts(contactList);

          this.patchFormFromContact(
            this.primaryContactFormGroup,
            this.primaryContact,
          );
          this.patchFormFromContact(
            this.supportContactFormGroup,
            this.supportContact,
          );
        },
        error: () => {
          this.isLoadingContacts = false;
          this.organizationContacts = [];
          this.existingContactIds.clear();
          this.contacts = [];
          this.primaryContact = null;
          this.supportContact = null;
          this.primaryContactFormGroup.reset();
          this.supportContactFormGroup.reset();
        },
      });
  }

  private buildContactFormGroup(): FormGroup {
    return this.fb.group({
      contactId: [''],
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      emailAddress: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', [Validators.required]],
      extension: [''],
    });
  }

  private patchFormFromContact(
    formGroup: FormGroup,
    contact: OrganizationContactDtoModel | null,
  ): void {
    if (!contact) {
      formGroup.reset({
        contactId: this.createNewContactValue,
        firstName: '',
        lastName: '',
        emailAddress: '',
        phoneNumber: '',
        extension: '',
      });
      return;
    }

    formGroup.patchValue({
      contactId:
        contact.contactId !== this.createNewContactValue
          ? (contact.contactId ?? this.createNewContactValue)
          : this.createNewContactValue,
      firstName: contact.firstName ?? '',
      lastName: contact.lastName ?? '',
      emailAddress: contact.emailAddress ?? '',
      phoneNumber: this.maskPhoneNumber(contact.phoneNumber),
      extension: contact.phoneNumberExtension ?? '',
    });
  }

  maskPhoneNumber(value?: string | null): string {
    const raw = value ?? '';
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return raw;
  }

  private findContactById(
    contactId: string,
  ): OrganizationContactDtoModel | null {
    if (!contactId) {
      return null;
    }

    return (
      this.organizationContacts.find((x) => x.contactId === contactId) ?? null
    );
  }

  private buildDropdownContacts(
    contacts: OrganizationContactDtoModel[],
  ): ContactDropdownItem[] {
    // Remove exact duplicate contact records (same normalized name, email, and phone).
    const unique = contacts.filter((contact, index, arr) => {
      // Build a normalized signature for the current candidate contact.
      const sig = this.getExactSignature(contact);
      // Keep only the first occurrence of each signature.
      return arr.findIndex((x) => this.getExactSignature(x) === sig) === index;
    });

    // Group remaining contacts by normalized first+last name.
    const groupedByName = new Map<string, OrganizationContactDtoModel[]>();
    // Build each name group so we can compare richer vs partial records within same person name.
    for (const contact of unique) {
      // Name key is used to place contacts into the same-name bucket.
      const nameKey = this.getNameKey(contact);
      // Get existing group or start an empty one.
      const list = groupedByName.get(nameKey) ?? [];
      // Add contact to its same-name group.
      list.push(contact);
      // Save the updated group back into the map.
      groupedByName.set(nameKey, list);
    }

    // Collect final contacts that should appear in the dropdown.
    const projected: OrganizationContactDtoModel[] = [];
    // Process each same-name group independently.
    for (const [, group] of groupedByName) {
      // Drop strict-subset rows (e.g., same person where one row has less info than another).
      const filtered = group.filter((candidate) => {
        // Keep a candidate only if no richer sibling in the group fully supersedes it.
        return !group.some(
          (other) =>
            other !== candidate && this.isStrictSubset(candidate, other),
        );
      });
      // Append this group's filtered records to the final collection.
      projected.push(...filtered);
    }

    // Sort the final records for stable and readable dropdown order.
    projected.sort((a, b) => this.compareForDisplay(a, b));

    // Convert internal contact records to the dropdown item format expected by Kendo.
    return (
      projected
        // Only include entries that have a usable contact id.
        .filter((contact) => !!contact.contactId)
        // Map each contact into display text and primitive id fields.
        .map((contact) => {
          // Compute normalized full name used in the label.
          const fullName = this.getFullName(contact);
          // Normalize email for consistent display and comparisons.
          const email = this.norm(contact.emailAddress);
          // Normalize phone for consistent display and comparisons.
          const phone = this.norm(contact.phoneNumber);
          // Start with empty details and fill it based on available fields.
          let details = '';

          // Prefer showing both email and phone when both exist.
          if (email && phone) {
            details = `${email} / ${phone}`;
            // Otherwise show email only.
          } else if (email) {
            details = email;
            // Otherwise show phone only.
          } else if (phone) {
            details = phone;
          }

          // Return the final dropdown item label and id.
          return {
            id: contact.contactId ?? '',
            name: details
              ? `${fullName} - ${details}`
              : fullName || 'Unknown Contact',
          };
        })
    );
  }

  private compareForDisplay(
    a: OrganizationContactDtoModel,
    b: OrganizationContactDtoModel,
  ): number {
    const nameCmp = this.getFullName(a).localeCompare(this.getFullName(b));
    if (nameCmp !== 0) {
      return nameCmp;
    }

    const emailCmp = this.norm(a.emailAddress).localeCompare(
      this.norm(b.emailAddress),
    );
    if (emailCmp !== 0) {
      return emailCmp;
    }

    return this.norm(a.phoneNumber).localeCompare(this.norm(b.phoneNumber));
  }

  private isStrictSubset(
    a: OrganizationContactDtoModel,
    b: OrganizationContactDtoModel,
  ): boolean {
    const aEmail = this.norm(a.emailAddress);
    const aPhone = this.norm(a.phoneNumber);
    const bEmail = this.norm(b.emailAddress);
    const bPhone = this.norm(b.phoneNumber);

    const compatible =
      (!aEmail || aEmail === bEmail) && (!aPhone || aPhone === bPhone);
    if (!compatible) {
      return false;
    }

    const bHasMore = (!aEmail && !!bEmail) || (!aPhone && !!bPhone);
    return bHasMore;
  }

  private getNameKey(
    contact: OrganizationContactDtoModel | ContactFormValue,
  ): string {
    return `${this.norm(contact.firstName)}|${this.norm(contact.lastName)}`;
  }

  private getExactSignature(
    contact: OrganizationContactDtoModel | ContactFormValue,
  ): string {
    return `${this.getNameKey(contact)}|${this.norm(contact.emailAddress)}|${this.norm(contact.phoneNumber)}`;
  }

  private getFullName(
    contact: OrganizationContactDtoModel | ContactFormValue,
  ): string {
    return `${this.norm(contact.firstName)} ${this.norm(contact.lastName)}`.trim();
  }

  private norm(value?: string | null): string {
    return (value ?? '').trim().toLowerCase();
  }

  private toFormValue(formGroup: FormGroup): ContactFormValue {
    const raw = formGroup.getRawValue();
    return {
      contactId: raw.contactId ?? '',
      firstName: (raw.firstName ?? '').trim(),
      lastName: (raw.lastName ?? '').trim(),
      emailAddress: (raw.emailAddress ?? '').trim(),
      phoneNumber: (raw.phoneNumber ?? '').trim(),
      extension: (raw.extension ?? '').trim(),
    };
  }

  private persistContactForTarget(target: ContactTarget): boolean {
    // Pick the correct form (primary/support) based on the target we are saving.
    const formGroup =
      target === 'primary'
        ? this.primaryContactFormGroup
        : this.supportContactFormGroup;
    // Normalize current form values into a simple object we can compare and persist.
    const formValue = this.toFormValue(formGroup);

    // If dropdown is not selected but any field is filled, treat as new contact creation
    const hasAnyFieldFilled =
      !!formValue.firstName ||
      !!formValue.lastName ||
      !!formValue.emailAddress ||
      !!formValue.phoneNumber;
    if (!formValue.contactId && hasAnyFieldFilled) {
      const created = this.createNewContactRecord(formValue, target);
      this.setTargetContact(target, created);
      return true;
    }

    // If the form currently points to an existing selected contact (and not the create-new sentinel), resolve it.
    const selectedExisting =
      formValue.contactId && formValue.contactId !== this.createNewContactValue
        ? this.findContactById(formValue.contactId)
        : null;

    // Handle the case where user started from an existing selected contact.
    if (
      selectedExisting &&
      formValue.firstName === (selectedExisting.firstName ?? '') &&
      formValue.lastName === (selectedExisting.lastName ?? '')
    ) {
      // Build normalized signatures to detect whether the user actually changed data.
      const selectedSignature = this.getExactSignature(selectedExisting);
      const editedSignature = this.getExactSignature(formValue);
      const selectedExtension = this.norm(
        selectedExisting.phoneNumberExtension,
      );
      const editedExtension = this.norm(formValue.extension);
      this.updateExistingContact(selectedExisting, formValue);
      this.setTargetContact(target, selectedExisting);
      return true;

      // If user edited details, clear contactId so matching logic treats this as new/matching input.
      //  formValue.contactId = '';
    }

    // Find all contacts with the same normalized first/last name.
    const nameMatches = this.organizationContacts.filter(
      (x) => this.getNameKey(x) === this.getNameKey(formValue),
    );
    // Find an exact match on name+email+phone.
    const exact = nameMatches.find(
      (x) => this.getExactSignature(x) === this.getExactSignature(formValue),
    );
    // If exact match exists, reuse it and continue.
    if (exact) {
      // Keep extension in sync even when the core signature already matches.
      if (
        this.norm(exact.phoneNumberExtension) !== this.norm(formValue.extension)
      ) {
        this.updateExistingContact(exact, formValue);
      }
      this.setTargetContact(target, exact);
      return true;
    }

    // Look for same-name contact with blank email+phone; this record can be enriched silently.
    const blankEmailPhone = nameMatches.find(
      (x) => !this.norm(x.emailAddress) && !this.norm(x.phoneNumber),
    );
    // If found, update that record with entered values and continue.
    if (blankEmailPhone) {
      this.updateExistingContact(blankEmailPhone, formValue);
      this.setTargetContact(target, blankEmailPhone);
      return true;
    }

    // Look for same-name+same-email where existing phone is blank; add phone silently.
    const sameNameEmailPhoneBlank = nameMatches.find(
      (x) =>
        this.norm(x.emailAddress) === this.norm(formValue.emailAddress) &&
        !this.norm(x.phoneNumber),
    );
    // If found, merge entered phone into this existing record and continue.
    if (sameNameEmailPhoneBlank) {
      this.updateExistingContact(sameNameEmailPhoneBlank, formValue);
      this.setTargetContact(target, sameNameEmailPhoneBlank);
      return true;
    }

    // Collect same-name+same-email candidates where phone conflicts (non-blank and different).
    const sameNameEmailDiffPhone = nameMatches.filter(
      (x) =>
        this.norm(x.emailAddress) === this.norm(formValue.emailAddress) &&
        !!this.norm(x.phoneNumber) &&
        this.norm(x.phoneNumber) !== this.norm(formValue.phoneNumber),
    );
    // If conflict exists, open dialog so user decides (use existing vs create new), then pause save.
    if (sameNameEmailDiffPhone.length) {
      this.openConflictDialog(
        'Existing contact(s) with the same name and email exist. Would you like to update the existing contact(s) instead?',
        target,
        formValue,
        this.distinctAndOrderedCandidates(
          sameNameEmailDiffPhone,
          formValue,
          'email',
        ),
      );
      return false;
    }

    // Collect same-name+same-phone candidates where email conflicts (non-blank and different).
    const sameNamePhoneDiffEmail = nameMatches.filter(
      (x) =>
        this.norm(x.phoneNumber) === this.norm(formValue.phoneNumber) &&
        !!this.norm(x.emailAddress) &&
        this.norm(x.emailAddress) !== this.norm(formValue.emailAddress),
    );
    // If conflict exists, open dialog for user decision and pause save.
    if (sameNamePhoneDiffEmail.length) {
      this.openConflictDialog(
        'Existing contact(s) with the same name and phone number exist. Would you like to update the existing contact(s) instead?',
        target,
        formValue,
        this.distinctAndOrderedCandidates(
          sameNamePhoneDiffEmail,
          formValue,
          'phone',
        ),
      );
      return false;
    }

    // If only name overlaps remain, ask user how to proceed.
    if (nameMatches.length) {
      console.log('Name-only matches:', nameMatches);
      this.openConflictDialog(
        'Existing contact(s) with the same name exist. Would you like to update the existing contact(s) instead?',
        target,
        formValue,
        this.distinctAndOrderedCandidates(nameMatches, formValue, 'mixed'),
      );
      return false;
    }

    // No usable match/conflict: create a brand-new contact record from form values.
    const created = this.createNewContactRecord(formValue, target);
    // Assign newly created contact to the requested target (and linked side when applicable).
    this.setTargetContact(target, created);
    // Target resolved successfully, save can proceed.
    return true;
  }

  private buildContactUpdates(): UpdateOrganizationContactDtoModel[] {
    const primaryId = this.primaryContactFormGroup.get('contactId')?.value as
      | string
      | undefined;
    const supportId = this.supportContactFormGroup.get('contactId')?.value as
      | string
      | undefined;

    if (
      primaryId &&
      supportId &&
      primaryId === supportId &&
      primaryId !== this.createNewContactValue &&
      this.existingContactIds.has(primaryId)
    ) {
      const sourceFormGroup = this.editPrimaryContact()
        ? this.primaryContactFormGroup
        : this.supportContactFormGroup;
      const formValue = this.toFormValue(sourceFormGroup);

      return [
        {
          contactId: primaryId,
          firstName: formValue.firstName,
          lastName: formValue.lastName,
          emailAddress: formValue.emailAddress,
          phoneNumber: formValue.phoneNumber,
          phoneNumberExtension: formValue.extension,
          isPrimaryContact: true,
          isSupportContact: true,
        },
      ];
    }

    const updates = new Map<string, UpdateOrganizationContactDtoModel>();
    this.addContactUpdate(
      updates,
      this.primaryContact,
      this.primaryContactFormGroup,
      true,
      false,
    );
    this.addContactUpdate(
      updates,
      this.supportContact,
      this.supportContactFormGroup,
      false,
      true,
    );
    return Array.from(updates.values());
  }

  private buildContactAdds(): CreateOrganizationContactDtoModel[] {
    const primaryId = this.primaryContactFormGroup.get('contactId')?.value as
      | string
      | undefined;
    const supportId = this.supportContactFormGroup.get('contactId')?.value as
      | string
      | undefined;

    if (
      primaryId &&
      supportId &&
      primaryId === supportId &&
      primaryId !== this.createNewContactValue &&
      !this.existingContactIds.has(primaryId)
    ) {
      const sourceFormGroup = this.editPrimaryContact()
        ? this.primaryContactFormGroup
        : this.supportContactFormGroup;
      const formValue = this.toFormValue(sourceFormGroup);

      return [
        {
          firstName: formValue.firstName,
          lastName: formValue.lastName,
          emailAddress: formValue.emailAddress,
          phoneNumber: formValue.phoneNumber,
          phoneNumberExtension: formValue.extension,
          isPrimaryContact: true,
          isSupportContact: true,
        },
      ];
    }

    const adds = new Map<string, CreateOrganizationContactDtoModel>();
    this.addContactCreate(
      adds,
      this.primaryContact,
      this.primaryContactFormGroup,
      true,
      false,
    );
    this.addContactCreate(
      adds,
      this.supportContact,
      this.supportContactFormGroup,
      false,
      true,
    );
    return Array.from(adds.values());
  }

  private addContactCreate(
    adds: Map<string, CreateOrganizationContactDtoModel>,
    contact: OrganizationContactDtoModel | null,
    formGroup: FormGroup,
    isPrimaryContact: boolean,
    isSupportContact: boolean,
  ): void {
    const contactId = contact?.contactId ?? '';
    if (!contactId || this.existingContactIds.has(contactId)) {
      return;
    }

    // New contacts (sentinel value) must use a role-based key so primary and support
    // don't overwrite each other in the Map. Real contact IDs still deduplicate normally.
    const mapKey =
      contactId === this.createNewContactValue
        ? isPrimaryContact
          ? '__new_primary__'
          : '__new_support__'
        : contactId;

    const formValue = this.toFormValue(formGroup);
    adds.set(mapKey, {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      emailAddress: formValue.emailAddress,
      phoneNumber: formValue.phoneNumber,
      phoneNumberExtension: formValue.extension,
      isPrimaryContact,
      isSupportContact,
    });
  }

  private addContactUpdate(
    updates: Map<string, UpdateOrganizationContactDtoModel>,
    contact: OrganizationContactDtoModel | null,
    formGroup: FormGroup,
    isPrimaryContact: boolean,
    isSupportContact: boolean,
  ): void {
    const contactId = contact?.contactId ?? '';
    if (!contactId || !this.existingContactIds.has(contactId)) {
      return;
    }

    const existing = updates.get(contactId);
    if (existing) {
      existing.isPrimaryContact = existing.isPrimaryContact || isPrimaryContact;
      existing.isSupportContact = existing.isSupportContact || isSupportContact;
      updates.set(contactId, existing);
      return;
    }

    const formValue = this.toFormValue(formGroup);
    updates.set(contactId, {
      contactId,
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      emailAddress: formValue.emailAddress,
      phoneNumber: formValue.phoneNumber,
      phoneNumberExtension: formValue.extension,
      isPrimaryContact,
      isSupportContact,
    });
  }

  private updateExistingContact(
    contact: OrganizationContactDtoModel,
    formValue: ContactFormValue,
  ): void {
    contact.firstName = formValue.firstName;
    contact.lastName = formValue.lastName;
    contact.emailAddress = formValue.emailAddress;
    contact.phoneNumber = formValue.phoneNumber;
    contact.phoneNumberExtension = formValue.extension;
    this.contacts = this.buildDropdownContacts(this.organizationContacts);
  }

  private createNewContactRecord(
    formValue: ContactFormValue,
    target: ContactTarget,
  ): OrganizationContactDtoModel {
    let created: OrganizationContactDtoModel;
    if (
      this.supportContact?.contactId === this.primaryContact?.contactId &&
      formValue.contactId !== this.createNewContactValue
    ) {
      created = {
        contactId: this.primaryContact?.contactId,
        accountName: this.authService.selectedAccount()?.name ?? null,
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        emailAddress: formValue.emailAddress,
        phoneNumber: formValue.phoneNumber,
        phoneNumberExtension: formValue.extension,
        isPrimaryContact: true,
        isSupportContact: true,
      };

      this.organizationContacts.push(created);
    } else if (formValue.contactId !== this.createNewContactValue) {
      created = {
        contactId:
          target === 'primary'
            ? this.primaryContact?.contactId
            : (this.supportContact?.contactId ?? this.generateId()),
        accountName: this.authService.selectedAccount()?.name ?? null,
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        emailAddress: formValue.emailAddress,
        phoneNumber: formValue.phoneNumber,
        phoneNumberExtension: formValue.extension,
        isPrimaryContact: target === 'primary',
        isSupportContact: target === 'support',
      };

      this.organizationContacts.push(created);
    } else {
      created = {
        contactId: this.createNewContactValue,
        accountName: this.authService.selectedAccount()?.name ?? null,
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        emailAddress: formValue.emailAddress,
        phoneNumber: formValue.phoneNumber,
        phoneNumberExtension: formValue.extension,
        isPrimaryContact: target === 'primary',
        isSupportContact: target === 'support',
      };
    }

    this.contacts = this.buildDropdownContacts(this.organizationContacts);
    return created;
  }

  private generateId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `generated-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
  }

  private setTargetContact(
    target: ContactTarget,
    contact: OrganizationContactDtoModel,
  ): void {
    if (target === 'primary') {
      this.primaryContact = contact;
      this.patchFormFromContact(this.primaryContactFormGroup, contact);
    } else {
      this.supportContact = contact;
      this.patchFormFromContact(this.supportContactFormGroup, contact);
    }

    if (
      this.areFormsLinked(
        this.primaryContactFormGroup,
        this.supportContactFormGroup,
      )
    ) {
      if (target === 'primary') {
        this.supportContact = contact;
        this.patchFormFromContact(this.supportContactFormGroup, contact);
      } else {
        this.primaryContact = contact;
        this.patchFormFromContact(this.primaryContactFormGroup, contact);
      }
    }
  }

  private distinctAndOrderedCandidates(
    candidates: OrganizationContactDtoModel[],
    formValue: ContactFormValue,
    mode: 'email' | 'phone' | 'mixed',
  ): OrganizationContactDtoModel[] {
    const unique = candidates.filter((candidate, index, arr) => {
      const sig = this.getExactSignature(candidate);
      return arr.findIndex((x) => this.getExactSignature(x) === sig) === index;
    });

    const reduced = unique.filter((candidate) => {
      return !unique.some(
        (other) => other !== candidate && this.isStrictSubset(candidate, other),
      );
    });

    return reduced.sort((a, b) => {
      const aEmailMatch =
        this.norm(a.emailAddress) === this.norm(formValue.emailAddress) ? 1 : 0;
      const bEmailMatch =
        this.norm(b.emailAddress) === this.norm(formValue.emailAddress) ? 1 : 0;
      const aPhoneMatch =
        this.norm(a.phoneNumber) === this.norm(formValue.phoneNumber) ? 1 : 0;
      const bPhoneMatch =
        this.norm(b.phoneNumber) === this.norm(formValue.phoneNumber) ? 1 : 0;

      if (mode === 'email' || mode === 'mixed') {
        if (aEmailMatch !== bEmailMatch) {
          return bEmailMatch - aEmailMatch;
        }
      }

      if (mode === 'phone' || mode === 'mixed') {
        if (aPhoneMatch !== bPhoneMatch) {
          return bPhoneMatch - aPhoneMatch;
        }
      }

      const emailCmp = this.norm(a.emailAddress).localeCompare(
        this.norm(b.emailAddress),
      );
      if (emailCmp !== 0) {
        return emailCmp;
      }

      return this.norm(a.phoneNumber).localeCompare(this.norm(b.phoneNumber));
    });
  }

  private openConflictDialog(
    message: string,
    target: ContactTarget,
    formValue: ContactFormValue,
    candidates: OrganizationContactDtoModel[],
  ): void {
    this.dialogMessage = message;
    this.dialogCandidates = candidates;
    this.pendingConflict = { target, formValue, candidates };
    this.showCandidateSelection = false;
    this.selectedDialogContactId = '';
    this.isDialogOpen = true;
  }

  private areFormsLinked(
    sourceFormGroup: FormGroup,
    targetFormGroup: FormGroup,
  ): boolean {
    const sourceContactId = sourceFormGroup.get('contactId')?.value;
    const targetContactId = targetFormGroup.get('contactId')?.value;

    if (!sourceContactId || !targetContactId) {
      return false;
    }

    if (
      sourceContactId === this.createNewContactValue ||
      targetContactId === this.createNewContactValue
    ) {
      return false;
    }

    return sourceContactId === targetContactId;
  }

  isNameInvalid(formGroup: FormGroup): boolean {
    const firstName = formGroup.get('firstName');
    const lastName = formGroup.get('lastName');

    return !!(
      (firstName?.touched && firstName.invalid) ||
      (lastName?.touched && lastName.invalid)
    );
  }

  isControlInvalid(formGroup: FormGroup, controlName: string): boolean {
    const control = formGroup.get(controlName);
    return !!(control?.touched && control.invalid);
  }

  getSelectedContactLabel(
    formGroup: FormGroup,
    dataItem?: ContactDropdownItem | null,
  ): string {
    const selectedValue = formGroup.get('contactId')?.value;

    if (selectedValue === this.createNewContactValue) {
      return '+ Create New Contact';
    }

    return dataItem?.name ?? '';
  }

  closeDialog(): void {
    this.isDialogOpen = false;
    this.showCandidateSelection = false;
    this.selectedDialogContactId = '';
    this.dialogCandidates = [];
    this.pendingConflict = null;
  }

  useExisting(): void {
    if (!this.pendingConflict) {
      this.closeDialog();
      return;
    }

    if (this.dialogCandidates.length <= 1) {
      const selected = this.dialogCandidates[0];
      if (selected) {
        this.setTargetContact(this.pendingConflict.target, selected);
      }
      this.closeDialog();
      return;
    }

    this.showCandidateSelection = true;
  }

  createNew(): void {
    if (this.pendingConflict) {
      const created = this.createNewContactRecord(
        this.pendingConflict.formValue,
        this.pendingConflict.target,
      );
      this.setTargetContact(this.pendingConflict.target, created);
    }
    this.closeDialog();
  }

  selectDialogContact(contactId: string): void {
    this.selectedDialogContactId = contactId;
  }

  applySelectedExisting(): void {
    if (!this.pendingConflict || !this.selectedDialogContactId) {
      return;
    }

    const selected = this.findContactById(this.selectedDialogContactId);
    if (selected) {
      this.setTargetContact(this.pendingConflict.target, selected);
    }

    this.closeDialog();
  }

  getDialogContactDisplay(contact: OrganizationContactDtoModel): string {
    const name = this.getFullName(contact);
    const email = this.norm(contact.emailAddress);
    const phone = this.norm(contact.phoneNumber);
    if (email && phone) {
      return `${name} - ${email} / ${phone}`;
    }
    if (email) {
      return `${name} - ${email}`;
    }
    if (phone) {
      return `${name} - ${phone}`;
    }
    return name;
  }
}
